import { useCallback, useEffect, useMemo, useState } from 'react';
import * as bd from 'react-basic-design';
import {
    AccessTeamRoleDTO,
    useDeleteAccessTeamRoleApi,
    useSaveAccessTeamRoleApi,
    useSearchAccessTeamRoleApi,
} from '../../../api/access-team-role-api';
import SvgAdd from '../../../assets/icons/Add';
import { InitFailed } from '../../../components/basic/init-failed';
import { Loading } from '../../../components/basic/loading';
import { notify } from '../../../components/basic/notify';
import { T, translate } from '../../../components/basic/text';
import { LoadingButton } from '../../../components/loading-button';
import { PageNavbar } from '../../../components/ui/page-navbar';
import { TProvider } from '../../../i18n';
import { IBasicTableColumn } from '../../../interfaces/interfaces';
import { SaveAccessTeamRoleModal } from './save-access-team-role-modal';

export function AccessTeamRoleApp() {
    const [showModal, setShowModal] = useState(false);
    const [selectedAccessTeamRole, setSelectedAccessTeamRole] = useState<
        AccessTeamRoleDTO | undefined
    >();
    const accessTeamRoleInit = useSearchAccessTeamRoleApi();
    const saveAccessTeamRole = useSaveAccessTeamRoleApi();
    const deleteAccessTeamRole = useDeleteAccessTeamRoleApi();

    const { result, error, busy, initialized, initializing } = accessTeamRoleInit;

    const accessRoleTitle = useCallback(
        (id?: string) => {
            var accessRole = result?.accessRoles.find(x => x.id === id);
            return <span>{accessRole ? `${accessRole.title} ` : id}</span>;
        },
        [result?.accessRoles]
    );

    const accessTeamTitle = useCallback(
        (serial?: number) => {
            var accessTeam = result?.accessTeams.find(x => x.serial === serial);
            return <span>{accessTeam ? `${accessTeam.title} ` : serial}</span>;
        },
        [result?.accessTeams]
    );

    const columns: IBasicTableColumn<AccessTeamRoleDTO>[] = useMemo(
        () => [
            {
                Header: <T>role-id</T>,
                name: 'roleId',
                width: 80,
                Cell: ({ row }) => accessRoleTitle(row.roleId),
            },
            {
                Header: <T>access-team-serial</T>,
                name: 'accessTeamSerial',
                width: 80,
                Cell: ({ row }) => accessTeamTitle(row.accessTeamSerial),
            },
            {
                Header: <T>permission-level</T>,
                name: 'permissionLevel',
                width: 80,
            },
        ],
        [accessRoleTitle, accessTeamTitle]
    );

    useEffect(() => {
        if (initialized || initializing || error) return;
        accessTeamRoleInit.post(null, {});
    }, [initialized, initializing, error, accessTeamRoleInit]);

    const handleDelete = (accessTeamRole: AccessTeamRoleDTO) => {
        bd.msgbox(
            translate('delete-access-team-role'),
            translate('confirm-delete-access-team-role'),
            [
                {
                    title: translate('delete'),
                    variant: 'contained',
                    color: 'secondary',
                    action: hide => {
                        deleteAccessTeamRole.remove(
                            accessTeamRole,
                            () => {
                                accessTeamRoleInit.updateResult({
                                    ...result,
                                    accessTeamRoles: result.accessTeamRoles.filter(
                                        b =>
                                            b.roleId !== accessTeamRole.roleId ||
                                            b.accessTeamSerial !== accessTeamRole.accessTeamSerial
                                    ),
                                });
                                notify.dark(
                                    translate('selected-access-team-role-deleted-successfully')
                                );
                                hide();
                                setShowModal(false);
                                setSelectedAccessTeamRole(undefined);
                            },
                            notify.error
                        );
                    },
                },
                {
                    title: translate('cancel'),
                    variant: 'outline',
                    color: 'secondary',
                },
            ],
            { className: 'modal-n', backdropClassName: 'modal-n' }
        );
    };

    const handleSave = (accessTeamRole: AccessTeamRoleDTO) => {
        saveAccessTeamRole.post(
            null,
            accessTeamRole,
            res => {
                if (selectedAccessTeamRole) {
                    accessTeamRoleInit.updateResult({
                        ...result,
                        accessTeamRoles: result.accessTeamRoles.map(b =>
                            b.roleId === accessTeamRole.roleId &&
                            b.accessTeamSerial === accessTeamRole.accessTeamSerial
                                ? res.accessTeamRole
                                : b
                        ),
                    });
                } else {
                    accessTeamRoleInit.updateResult({
                        ...result,
                        accessTeamRoles: [res.accessTeamRole, ...result.accessTeamRoles],
                    });
                }

                setShowModal(false);
                setSelectedAccessTeamRole(undefined);
                notify.success(
                    selectedAccessTeamRole
                        ? translate('selected-access-team-role-updated-successfully')
                        : translate('new-access-team-role-created-successfully')
                );
            },
            notify.error
        );
    };

    if (error) return <InitFailed ex={error} onRetry={() => accessTeamRoleInit.post(null, {})} />;
    if (!initialized || initializing) return <Loading />;

    return (
        <TProvider>
            <div className="container">
                <PageNavbar
                    onGoBack="/home"
                    title="access-team-role"
                    controls={
                        <LoadingButton
                            variant="contained"
                            size="sm"
                            label={<T>new-access-team-role</T>}
                            icon={<SvgAdd />}
                            onClick={() => {
                                setShowModal(true);
                                setSelectedAccessTeamRole(undefined);
                            }}
                            loading={busy}
                        />
                    }
                />
                <bd.Paper className="h-100">
                    <bd.BasicTable
                        columns={columns}
                        data={result.accessTeamRoles}
                        onRowClick={({ row }) => {
                            setSelectedAccessTeamRole(row);
                            setShowModal(true);
                        }}
                        hover
                    />

                    <SaveAccessTeamRoleModal
                        show={showModal}
                        onHide={() => {
                            setShowModal(false);
                            setSelectedAccessTeamRole(undefined);
                        }}
                        onSave={handleSave}
                        onDelete={handleDelete}
                        selectedAccessTeamRole={selectedAccessTeamRole}
                        accessRoles={result?.accessRoles}
                        accessTeams={result?.accessTeams}
                        allowEdit={result.allowEdit}
                    />
                </bd.Paper>
            </div>
        </TProvider>
    );
}
